/**
 * Role Packages Settings Page
 *
 * Browse built-in and custom multi-role assignment sets and apply them to
 * users or groups. Admins can create, edit, and delete custom packages.
 *
 * Built-in packages: defined in src/types/prioritization/rolePackages.ts (read-only)
 * Custom packages:   persisted in the role_packages DB table
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Package,
  ChevronDown,
  ChevronUp,
  Users,
  User,
  Search,
  X,
  Check,
  Loader2,
  Info,
  Plus,
  Pencil,
  Trash2,
  Lock,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  ROLE_PACKAGES,
  ROLE_PACKAGE_THEME_LABELS,
  getRoleSlugTier,
  type RolePackageDefinition,
} from '../../../types/prioritization/rolePackages';
import { DEFAULT_JOB_ROLES } from '../../../types/prioritization/jobRoles';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getSupabaseClient, isSupabaseConfigured } from '../../../lib/supabaseClient';
import { createRoleAssignment } from '../../../lib/rolesService';
import { useUserStore } from '../../../stores/prioritization/userStore';
import {
  getCustomRolePackages,
  createCustomRolePackage,
  updateCustomRolePackage,
  deleteCustomRolePackage,
  type CustomRolePackage,
} from '../../../lib/rolePackagesService';

// ============================================================================
// Types
// ============================================================================

interface WorkspaceMember {
  id: string;
  name: string;
  email: string;
  type: 'user';
}

interface WorkspaceGroup {
  id: string;
  name: string;
  type: 'group';
}

type AssignTarget = (WorkspaceMember | WorkspaceGroup) & { type: 'user' | 'group' };

type ThemeFilter = RolePackageDefinition['theme'] | 'all';

/** Unified view of built-in or custom package for rendering */
interface PackageRow {
  slug: string;
  displayName: string;
  description: string;
  roleSlug: string[];
  typicalPersona: string;
  theme: RolePackageDefinition['theme'];
  isSystem: boolean;
  customId?: string;
}

// ============================================================================
// Constants
// ============================================================================

const THEME_ORDER: Array<RolePackageDefinition['theme']> = [
  'backlog',
  'measurement',
  'sessions',
  'cycles',
  'governance',
  'platform',
  'cross-functional',
];

const THEME_OPTIONS = THEME_ORDER.map((t) => ({
  value: t,
  label: ROLE_PACKAGE_THEME_LABELS[t],
}));

// ============================================================================
// Helpers
// ============================================================================

function getRoleDetails(slug: string) {
  return DEFAULT_JOB_ROLES.find((r) => r.slug === slug);
}

function customToRow(c: CustomRolePackage): PackageRow {
  return {
    slug: c.id,
    displayName: c.name,
    description: c.description ?? '',
    roleSlug: c.roleSlugs,
    typicalPersona: c.typicalPersona ?? '',
    theme: c.theme,
    isSystem: false,
    customId: c.id,
  };
}

function builtInToRow(p: RolePackageDefinition): PackageRow {
  return {
    slug: p.slug,
    displayName: p.displayName,
    description: p.description,
    roleSlug: p.roleSlug,
    typicalPersona: p.typicalPersona,
    theme: p.theme,
    isSystem: true,
  };
}

// ============================================================================
// Assign Modal
// ============================================================================

function AssignModal({
  pkg,
  onClose,
}: {
  pkg: PackageRow;
  onClose: () => void;
}) {
  const currentUserId = useUserStore((s) => s.userId);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const [query, setQuery] = useState('');
  const [subjectType, setSubjectType] = useState<'user' | 'group'>('user');
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const [assignedCount, setAssignedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const allTargets = useMemo<AssignTarget[]>(
    () => (subjectType === 'user' ? members : (groups as AssignTarget[])),
    [subjectType, members, groups],
  );

  function toggleTarget(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAll() {
    setSelectedIds(new Set(filtered.map((t) => t.id)));
  }

  function clearAll() {
    setSelectedIds(new Set());
  }

  useEffect(() => {
    void loadTargets();
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  async function loadTargets() {
    if (!isSupabaseConfigured() || !workspaceId) { setLoadingTargets(false); return; }
    const supabase = getSupabaseClient();

    // Resolve user IDs for this workspace via workspace_memberships — the users
    // table has no workspace_id column; membership lives in the join table.
    const [membershipsResult, groupsResult] = await Promise.all([
      supabase
        .from('workspace_memberships')
        .select('user_id')
        .eq('workspace_id', workspaceId),
      supabase.from('groups').select('id, name').eq('workspace_id', workspaceId).order('name'),
    ]);

    if (membershipsResult.data && membershipsResult.data.length > 0) {
      const userIds = membershipsResult.data.map((m: { user_id: string }) => m.user_id);
      const { data: usersData } = await supabase
        .from('users')
        .select('id, name, email')
        .in('id', userIds)
        .order('name');
      setMembers(
        ((usersData ?? []) as { id: string; name: string | null; email: string }[]).map((u) => ({
          id: u.id, name: u.name ?? u.email, email: u.email, type: 'user' as const,
        })),
      );
    }

    if (groupsResult.data) {
      setGroups(
        (groupsResult.data as { id: string; name: string }[]).map((g) => ({
          id: g.id, name: g.name, type: 'group' as const,
        })),
      );
    }
    setLoadingTargets(false);
  }

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return allTargets;
    return allTargets.filter(
      (t) => t.name.toLowerCase().includes(q) || ('email' in t && t.email.toLowerCase().includes(q)),
    );
  }, [query, allTargets]);

  async function handleAssign() {
    if (selectedIds.size === 0 || !workspaceId || !currentUserId) return;
    setAssigning(true);
    setErrorMsg('');
    const supabase = getSupabaseClient();
    const errors: string[] = [];

    // Resolve role IDs once, then fan out across all selected targets
    const resolvedRoles: { slug: string; id: string; tier: 'primary' | 'supporting' }[] = [];
    for (const roleSlug of pkg.roleSlug) {
      const { data: roleRow } = await supabase
        .from('roles').select('id').eq('name', roleSlug).maybeSingle();
      if (!roleRow?.id) { errors.push(`Role not found: ${roleSlug}`); continue; }
      resolvedRoles.push({ slug: roleSlug, id: roleRow.id, tier: getRoleSlugTier(pkg, roleSlug) });
    }

    const targets = allTargets.filter((t) => selectedIds.has(t.id));
    for (const target of targets) {
      for (const { id: roleId, tier } of resolvedRoles) {
        try {
          await createRoleAssignment(
            { subjectType: target.type, subjectId: target.id, roleId, scopeKind: 'global', scopeId: workspaceId, tier },
            currentUserId,
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          if (!msg.toLowerCase().includes('duplicate') && !msg.toLowerCase().includes('unique')) {
            errors.push(`${target.name} / ${roleId}: ${msg}`);
          }
        }
      }
    }

    setAssigning(false);
    if (errors.length === 0) { setAssignedCount(targets.length); }
    else { setErrorMsg(errors.join('\n')); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] p-6 mx-4">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Assign "{pkg.displayName}"
            </h2>
            <p className="mt-0.5 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
              Applies {pkg.roleSlug.length} role{pkg.roleSlug.length !== 1 ? 's' : ''} to each selected {subjectType}.
            </p>
          </div>
          <button onClick={onClose} className="shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--surface-raised)]">
            <X className="size-4" />
          </button>
        </div>

        {assignedCount > 0 ? (
          <div className="bg-[var(--green-dim)] dark:bg-[var(--green)]/20 border border-[var(--green)] dark:border-[var(--green)] p-4 flex gap-3 items-start">
            <Check className="size-5 text-[var(--green)] dark:text-[var(--text-ter)] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
                Package applied to {assignedCount} {subjectType}{assignedCount !== 1 ? 's' : ''}
              </p>
              <p className="mt-1 text-xs text-[var(--green)] dark:text-[var(--text-ter)]">
                {pkg.roleSlug.length} role{pkg.roleSlug.length !== 1 ? 's' : ''} each. Existing assignments were kept.
              </p>
              <button onClick={onClose} className="mt-3 bg-[var(--green)] px-3 py-1.5 text-xs font-medium text-white hover:bg-[var(--green)]">Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex border border-[var(--rule)] dark:border-[var(--rule)] overflow-hidden mb-4">
              {(['user', 'group'] as const).map((t) => (
                <button key={t} type="button" onClick={() => { setSubjectType(t); setSelectedIds(new Set()); setQuery(''); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium transition-colors ${subjectType === t ? 'bg-[var(--slate)] text-white' : 'bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)]'}`}>
                  {t === 'user' ? <User className="size-3.5" /> : <Users className="size-3.5" />}
                  {t === 'user' ? 'User' : 'Group'}
                </button>
              ))}
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-ter)]" />
              <input ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder={subjectType === 'user' ? 'Search users…' : 'Search groups…'}
                className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] pl-8 pr-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
            </div>
            {/* Select all / clear row */}
            {filtered.length > 1 && (
              <div className="flex items-center justify-between px-1 mb-1">
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">
                  {selectedIds.size > 0 ? `${selectedIds.size} selected` : `${filtered.length} ${subjectType}s`}
                </span>
                <div className="flex gap-2">
                  {selectedIds.size < filtered.length && (
                    <button type="button" onClick={selectAll} className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline">Select all</button>
                  )}
                  {selectedIds.size > 0 && (
                    <button type="button" onClick={clearAll} className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:underline">Clear</button>
                  )}
                </div>
              </div>
            )}
            <div className="max-h-52 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {loadingTargets ? (
                <div className="flex items-center justify-center py-8 text-[var(--text-ter)]"><Loader2 className="size-4 animate-spin mr-2" /><span className="text-sm">Loading…</span></div>
              ) : filtered.length === 0 ? (
                <p className="py-6 text-center text-sm text-[var(--text-ter)] dark:text-[var(--text-dim)]">No {subjectType === 'user' ? 'users' : 'groups'} found.</p>
              ) : filtered.map((t) => {
                const isSelected = selectedIds.has(t.id);
                return (
                  <button key={t.id} type="button" onClick={() => toggleTarget(t.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${isSelected ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20' : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)]'}`}>
                    {/* Checkbox */}
                    <span className={`size-4 rounded border-2 shrink-0 flex items-center justify-center transition-colors ${isSelected ? 'border-[var(--slate)] bg-[var(--slate)]' : 'border-[var(--rule)]'}`}>
                      {isSelected && <Check className="size-2.5 text-white" strokeWidth={3} />}
                    </span>
                    <span className={`size-7 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold ${isSelected ? 'bg-[var(--slate)] text-white' : 'bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-sec)] dark:text-[var(--text-sec)]'}`}>
                      {t.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{t.name}</p>
                      {'email' in t && <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] truncate">{t.email}</p>}
                    </div>
                  </button>
                );
              })}
            </div>
            {errorMsg && (
              <div className="mt-3 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] p-3">
                <p className="text-xs font-medium text-[var(--coral)] dark:text-[var(--coral)] mb-1">Some roles could not be assigned:</p>
                <pre className="text-xs text-[var(--coral)] dark:text-[var(--coral)] whitespace-pre-wrap">{errorMsg}</pre>
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={onClose} className="border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)]">Cancel</button>
              <button disabled={selectedIds.size === 0 || assigning} onClick={() => void handleAssign()}
                className="bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                {assigning && <Loader2 className="size-3.5 animate-spin" />}
                {selectedIds.size > 1 ? `Assign to ${selectedIds.size} ${subjectType}s` : 'Assign package'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Create / Edit Modal
// ============================================================================

function EditPackageModal({
  initial,
  onClose,
  onSave,
}: {
  initial?: PackageRow;
  onClose: () => void;
  onSave: (pkg: PackageRow) => void;
}) {
  const currentUserId = useUserStore((s) => s.userId);
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const isEdit = !!initial;

  const [name, setName] = useState(initial?.displayName ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [theme, setTheme] = useState<RolePackageDefinition['theme']>(initial?.theme ?? 'cross-functional');
  const [typicalPersona, setTypicalPersona] = useState(initial?.typicalPersona ?? '');
  const [roleSearch, setRoleSearch] = useState('');
  const [selectedSlugs, setSelectedSlugs] = useState<string[]>(initial?.roleSlug ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const allRoles = DEFAULT_JOB_ROLES;

  const filteredRoles = useMemo(() => {
    const q = roleSearch.toLowerCase().trim();
    if (!q) return allRoles;
    return allRoles.filter(
      (r) => r.displayName.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || r.family.includes(q),
    );
  }, [roleSearch]);

  function toggleSlug(slug: string) {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  }

  async function handleSave() {
    if (!name.trim()) { setError('Package name is required.'); return; }
    if (selectedSlugs.length === 0) { setError('Select at least one role.'); return; }
    if (!workspaceId) { setError('No workspace found.'); return; }
    setSaving(true);
    setError('');
    try {
      let saved: PackageRow;
      if (isEdit && initial?.customId) {
        const updated = await updateCustomRolePackage(initial.customId, {
          name: name.trim(),
          description: description.trim() || undefined,
          theme,
          roleSlugs: selectedSlugs,
          typicalPersona: typicalPersona.trim() || undefined,
        });
        saved = customToRow(updated);
      } else {
        const created = await createCustomRolePackage({
          workspaceId,
          name: name.trim(),
          description: description.trim() || undefined,
          theme,
          roleSlugs: selectedSlugs,
          typicalPersona: typicalPersona.trim() || undefined,
          createdBy: currentUserId ?? undefined,
        });
        saved = customToRow(created);
      }
      onSave(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] mx-4 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)] shrink-0">
          <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            {isEdit ? 'Edit package' : 'New role package'}
          </h2>
          <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--surface-raised)]">
            <X className="size-4" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1">
              Package name <span className="text-[var(--coral)]">*</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Scoring Office"
              className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What does this package enable? Who should receive it?"
              className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Theme + Persona */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1">Theme</label>
              <select
                value={theme}
                onChange={(e) => setTheme(e.target.value as RolePackageDefinition['theme'])}
                className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {THEME_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] mb-1">Typical persona</label>
              <input
                value={typicalPersona}
                onChange={(e) => setTypicalPersona(e.target.value)}
                placeholder="e.g. PMO intake coordinator"
                className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Role picker */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">
                Roles <span className="text-[var(--coral)]">*</span>
              </label>
              {selectedSlugs.length > 0 && (
                <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] font-medium">
                  {selectedSlugs.length} selected
                </span>
              )}
            </div>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-ter)]" />
              <input
                value={roleSearch}
                onChange={(e) => setRoleSearch(e.target.value)}
                placeholder="Filter roles…"
                className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] pl-8 pr-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="max-h-52 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {filteredRoles.map((role) => {
                const checked = selectedSlugs.includes(role.slug);
                return (
                  <button
                    key={role.slug}
                    type="button"
                    onClick={() => toggleSlug(role.slug)}
                    className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors ${checked ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20' : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)]'}`}
                  >
                    <span className={`mt-0.5 size-4 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-[var(--slate)] border-[var(--slate)]' : 'border-[var(--rule)] dark:border-[var(--rule)]'}`}>
                      {checked && <Check className="size-2.5 text-white" />}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{role.displayName}</p>
                      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] leading-snug">{role.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <p className="text-sm text-[var(--coral)] dark:text-[var(--coral)]">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-[var(--rule)] dark:border-[var(--rule)] shrink-0">
          <button onClick={onClose} className="border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)]">
            Cancel
          </button>
          <button
            disabled={saving}
            onClick={() => void handleSave()}
            className="bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && <Loader2 className="size-3.5 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create package'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Delete Confirm Modal
// ============================================================================

function DeleteConfirmModal({
  pkg,
  onClose,
  onConfirm,
}: {
  pkg: PackageRow;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    if (!pkg.customId) return;
    setDeleting(true);
    try {
      await deleteCustomRolePackage(pkg.customId);
      onConfirm();
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative w-full max-w-sm bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-2xl border border-[var(--rule)] dark:border-[var(--rule)] p-6 mx-4">
        <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-2">
          Delete "{pkg.displayName}"?
        </h2>
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)] mb-5">
          This removes the package definition. Roles already assigned through this package are not affected.
        </p>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="border border-[var(--rule)] dark:border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)]">
            Cancel
          </button>
          <button
            disabled={deleting}
            onClick={() => void handleDelete()}
            className="bg-[var(--coral)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--coral)] disabled:opacity-40 flex items-center gap-2"
          >
            {deleting && <Loader2 className="size-3.5 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Package Card
// ============================================================================

function PackageCard({
  pkg,
  onAssign,
  onEdit,
  onDelete,
}: {
  pkg: PackageRow;
  onAssign: (pkg: PackageRow) => void;
  onEdit?: (pkg: PackageRow) => void;
  onDelete?: (pkg: PackageRow) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] overflow-hidden flex flex-col">
      {/* Card header */}
      <div className="p-4 flex-1">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 p-2">
            <Package className="size-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] leading-snug">
                {pkg.displayName}
              </h3>
              {pkg.isSystem ? (
                <Lock className="size-3 text-[var(--text-ter)] dark:text-[var(--text-dim)] shrink-0 mt-0.5" title="Built-in package" />
              ) : (
                <div className="flex gap-1 shrink-0">
                  {onEdit && (
                    <button
                      type="button"
                      onClick={() => onEdit(pkg)}
                      className="rounded p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--surface-raised)]"
                      title="Edit package"
                    >
                      <Pencil className="size-3" />
                    </button>
                  )}
                  {onDelete && (
                    <button
                      type="button"
                      onClick={() => onDelete(pkg)}
                      className="rounded p-1 text-[var(--text-ter)] hover:text-[var(--coral)] hover:bg-[var(--coral-dim)] dark:hover:bg-[var(--coral)]/20"
                      title="Delete package"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  )}
                </div>
              )}
            </div>
            {pkg.description && (
              <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] leading-relaxed">
                {pkg.description}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <span className="inline-flex items-center rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {pkg.roleSlug.length} role{pkg.roleSlug.length !== 1 ? 's' : ''}
          </span>
          {pkg.typicalPersona && (
            <>
              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)]">·</span>
              <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] truncate">{pkg.typicalPersona}</span>
            </>
          )}
          {!pkg.isSystem && (
            <span className="ml-auto inline-flex items-center rounded-full bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)] px-2 py-0.5 text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
              Custom
            </span>
          )}
        </div>
      </div>

      {/* Expandable role list */}
      <div className="border-t border-[var(--rule)] dark:border-[var(--rule)]">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface-raised)] transition-colors"
        >
          <span>Included roles</span>
          {expanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
        </button>
        {expanded && (
          <div className="px-4 pb-3 space-y-1.5">
            {pkg.roleSlug.map((slug) => {
              const role = getRoleDetails(slug);
              return (
                <div key={slug} className="flex items-start gap-2 bg-[var(--surface)] dark:bg-[var(--surface-raised)] px-3 py-2">
                  <div className="mt-1 size-1.5 rounded-full bg-[var(--slate)] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                      {role?.displayName ?? slug}
                    </p>
                    {role?.description && (
                      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] leading-snug">{role.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Assign button */}
      <div className="border-t border-[var(--rule)] dark:border-[var(--rule)] px-4 py-2.5">
        <button
          type="button"
          onClick={() => onAssign(pkg)}
          className="w-full bg-[var(--slate)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] transition-colors"
        >
          Assign to user or group
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function RolePackages() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);

  const [search, setSearch] = useState('');
  const [themeFilter, setThemeFilter] = useState<ThemeFilter>('all');
  const [customPackages, setCustomPackages] = useState<PackageRow[]>([]);
  const [loadingCustom, setLoadingCustom] = useState(true);

  const [assignTarget, setAssignTarget] = useState<PackageRow | null>(null);
  const [editTarget, setEditTarget] = useState<PackageRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PackageRow | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => {
    if (!workspaceId) { setLoadingCustom(false); return; }
    void getCustomRolePackages(workspaceId)
      .then((rows) => setCustomPackages(rows.map(customToRow)))
      .finally(() => setLoadingCustom(false));
  }, [workspaceId]);

  const allPackages = useMemo<PackageRow[]>(() => [
    ...customPackages,
    ...ROLE_PACKAGES.map(builtInToRow),
  ], [customPackages]);

  const themes = useMemo(
    () => THEME_ORDER.filter((t) => allPackages.some((p) => p.theme === t)),
    [allPackages],
  );

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return allPackages.filter((p) => {
      const themeOk = themeFilter === 'all' || p.theme === themeFilter;
      if (!themeOk) return false;
      if (!q) return true;
      return (
        p.displayName.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.typicalPersona.toLowerCase().includes(q) ||
        p.roleSlug.some((s) => s.includes(q))
      );
    });
  }, [search, themeFilter, allPackages]);

  const grouped = useMemo(() => {
    if (themeFilter !== 'all') return [{ theme: themeFilter, packages: filtered }];
    return themes
      .map((theme) => ({ theme, packages: filtered.filter((p) => p.theme === theme) }))
      .filter((g) => g.packages.length > 0);
  }, [filtered, themes, themeFilter]);

  const handleSaved = useCallback((saved: PackageRow) => {
    setCustomPackages((prev) => {
      const idx = prev.findIndex((p) => p.slug === saved.slug);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [saved, ...prev];
    });
    setShowCreate(false);
    setEditTarget(null);
  }, []);

  const handleDeleted = useCallback(() => {
    if (!deleteTarget?.customId) return;
    setCustomPackages((prev) => prev.filter((p) => p.customId !== deleteTarget.customId));
    setDeleteTarget(null);
  }, [deleteTarget]);

  return (
    <div className="pp-settings">
      <PageHeader
        title="Role Packages"
        subtitle="Named role bundles — apply a curated set of job roles to a user or group in one action."
        icon={<Package className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
      />

      {/* Info banner */}
      <div className="mb-6 flex gap-3 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 p-4">
        <Info className="size-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5 shrink-0" />
        <p className="text-sm text-[var(--text-sec)]">
          Packages assign the same individual roles you'd find under <strong>Roles</strong>. Applying a package to someone who already holds some roles is safe — existing assignments are preserved. Custom packages (amber badge) can be edited or deleted; built-in packages{' '}
          <Lock className="inline size-3 mb-0.5" /> are read-only.
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-[var(--text-ter)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search packages…"
            className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] pl-8 pr-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setThemeFilter('all')}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${themeFilter === 'all' ? 'bg-[var(--slate)] text-white' : 'bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--rule)]'}`}>
            All
          </button>
          {themes.map((t) => (
            <button key={t} type="button" onClick={() => setThemeFilter(t)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${themeFilter === t ? 'bg-[var(--slate)] text-white' : 'bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--rule)]'}`}>
              {ROLE_PACKAGE_THEME_LABELS[t]}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] shrink-0"
        >
          <Plus className="size-4" />
          New package
        </button>
      </div>

      {/* Loading */}
      {loadingCustom && (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] mb-4">
          <Loader2 className="size-4 animate-spin" /> Loading custom packages…
        </div>
      )}

      {/* Empty state */}
      {!loadingCustom && grouped.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Package className="size-10 text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-3" />
          <p className="text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-sec)]">No packages match your search.</p>
          <button onClick={() => { setSearch(''); setThemeFilter('all'); }} className="mt-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:underline">
            Clear filters
          </button>
        </div>
      )}

      {/* Package groups */}
      {grouped.map(({ theme, packages }) => (
        <div key={theme} className="mb-8">
          {themeFilter === 'all' && (
            <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-dim)] mb-3">
              {ROLE_PACKAGE_THEME_LABELS[theme]}
            </h2>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.slug}
                pkg={pkg}
                onAssign={setAssignTarget}
                onEdit={pkg.isSystem ? undefined : setEditTarget}
                onDelete={pkg.isSystem ? undefined : setDeleteTarget}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Modals */}
      {assignTarget && <AssignModal pkg={assignTarget} onClose={() => setAssignTarget(null)} />}
      {(showCreate || editTarget) && (
        <EditPackageModal
          initial={editTarget ?? undefined}
          onClose={() => { setShowCreate(false); setEditTarget(null); }}
          onSave={handleSaved}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          pkg={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleted}
        />
      )}
    </div>
  );
}
